<template>
  <div>
    <div class="relative flex flex-col py-2">
      <div class="mx-4">
        <p
          v-if="selectedExtraColumn"
          class="mt-1 mb-3 ml-3 text-xl text-left text-sc-gray-dark"
        >
          {{ $t('message.settings.columnsTab.editColumn') }}
        </p>
        <p
          v-else
          class="mt-1 mb-3 ml-3 text-xl text-left text-sc-gray-dark"
        >
          {{ $t('message.settings.columnsTab.addColumn') }}
        </p>
      </div>
      <div class="z-0 w-full border-b border-sc-gray-light" />
      <div
        class="grid grid-cols-2 border-b divide-x divide-sc-gray-light border-sc-gray-light"
      >
        <div>
          <p
            class="py-1 ml-5 font-semibold text-2xs text-primary"
          >
            {{ $t('message.settings.columnsTab.table.header.type').toUpperCase() }}
          </p>
          <div class="flex flex-col py-3 pl-5 border-t-2 border-sc-gray-light">
            <b-radio
              v-model="newExtraColumnType"
              v-for="(type, key) in newExtraColumnTypeMap"
              :key="key"
              @input="setNewExtraColumnType(key)"
              :native-value="key"
              :disabled="Boolean(selectedExtraColumn)"
              class="py-2 text-sm text-primary"
            >
              {{ type['name'] }}
            </b-radio>
          </div>
        </div>
        <div>
          <p
            class="py-1 ml-5 font-semibold text-2xs text-primary"
          >
            {{ $t('message.settings.columnsTab.table.header.name').toUpperCase() }}
          </p>
          <div
            class="border-t-2 border-b border-sc-gray-light"
          >
            <div
              class="flex-col mx-5 my-5 border-none appearance-none focus:outline-none"
            >
              <b-input
                type="text"
                :placeholder="$t('message.settings.columnsTab.modal.namePlaceholder')"
                aria-label="name"
                v-model="newExtraColumnName"
                size="is-small"
                class="text-xs"
              />
              <p
                v-if="newExtraColumnNameBlank && showErrors"
                class="text-xs text-sc-red-dark"
              >
                {{ $t('message.settings.columnsTab.modal.errors.blankName') }}
              </p>
            </div>
          </div>
          <div
            v-if="newExtraColumnType === 'Enum'"
          >
            <p
              class="py-1 ml-5 font-semibold text-2xs text-primary"
            >
              {{ $t('message.settings.columnsTab.table.header.options').toUpperCase() }}
            </p>
            <div
              class="border-t-2 border-b border-sc-gray-light"
            >
              <b-field
                class="mx-5 my-5 border-none appearance-none focus:outline-none"
              >
                <b-taglist
                  v-if="selectedExtraColumn && selectedExtraColumn.type === 'Enum'"
                >
                  <b-tag
                    v-for="(tag, key) in selectedExtraColumn.options.tags"
                    :key="key"
                    type="is-primary"
                    rounded
                  >
                    {{ tag }}
                  </b-tag>
                </b-taglist>
              </b-field>
              <div
                class="flex-col mx-5 my-5 border-none appearance-none focus:outline-none"
              >
                <b-taginput
                  :placeholder="$t('message.settings.columnsTab.modal.optionsPlaceholder')"
                  aria-label="options"
                  :before-adding="validateTag"
                  v-model="newExtraColumnOptions"
                  size="is-small"
                  type="is-primary"
                  class="text-xs"
                  rounded
                />
                <p
                  v-if="newExtraTagEmpty && showErrors"
                  class="text-xs text-sc-red-dark"
                >
                  {{ $t('message.settings.columnsTab.modal.errors.emptyTags') }}
                </p>
              </div>
            </div>
          </div>
          <p
            class="py-1 ml-5 font-semibold text-2xs text-primary"
          >
            {{ $t('message.settings.columnsTab.table.header.defaultValue').toUpperCase() }}
          </p>
          <div
            class="border-t-2 border-sc-gray-light"
          >
            <b-field
              class="mx-5 my-5 border-none appearance-none focus:outline-none"
            >
              <component
                :is="defaultValueComponent.create.component"
                v-bind="defaultValueComponent.create.props()"
                v-model="newExtraColumnDefault"
              >
                <span
                  v-if="newExtraColumnType === 'Enum'"
                  slot="caret"
                  class="absolute right-0 mt-3 mr-4"
                >
                  <img
                    class="transform chevron"
                    :src="require('@assets/images/chevron-down-outline.svg')"
                  >
                </span>
              </component>
            </b-field>
          </div>
        </div>
      </div>
      <div
        class="flex justify-between pt-3 pb-1 mx-5"
      >
        <p class="text-sm text-sc-red">
          {{ extraColumnError }}
        </p>
        <button
          ref="edit-extra-column-button"
          v-if="selectedExtraColumn"
          class="px-4 py-1 text-sm leading-5 text-center text-white rounded justify-content-center bg-sc-blue hover:text-white"
          @click="editExtraColumn"
        >
          {{ $t('message.settings.columnsTab.modal.editButton') }}
        </button>
        <button
          v-else
          ref="add-extra-column-button"
          class="px-4 py-1 text-sm leading-5 text-center text-white rounded justify-content-center bg-sc-blue hover:text-white"
          @click="addExtraColumn"
        >
          {{ $t('message.settings.columnsTab.modal.addButton') }}
        </button>
      </div>
    </div>
  </div>
</template>
<script>
import EntityConfigurationsApi from '../api/entity_configurations';

export default {
  data() {
    return {
      loading: false,
      newExtraColumnName: '',
      newExtraColumnDefault: '',
      newExtraColumnOptions: [],
      extraColumnOptions: [],
      extraColumnError: '',
      newExtraColumnType: 'String',
      showErrors: false,
      newExtraColumnTypeMap: {
        'Boolean': {
          name: this.$t('message.settings.columnsTab.types.toggle'), default: false,
          create: { component: 'b-checkbox', props: () => null },
          show: { component: 'b-icon',
            props: (row) => ({ type: 'is-primary', icon: row.default ? 'checkbox-marked' : 'checkbox-blank' }),
          },
        },
        'Date': {
          name: this.$t('message.settings.columnsTab.types.date'), default: null,
          create: { component: 'b-datepicker', props: () => ({
            placeholder: this.$t('message.settings.columnsTab.modal.optionalDefaultValuePlaceholder'),
            size: 'is-small',
            firstDayOfWeek: 1,
            editable: true,
          }) },
          show: { component: 'span', props: () => null },
        },
        'Enum': {
          name: this.$t('message.settings.columnsTab.types.tags'), default: '-',
          create: { component: 'multiselect', props: () => ({
            placeholder: this.$t('message.settings.columnsTab.modal.defaultValuePlaceholder'),
            options: ['-', ...Object.values(this.extraColumnOptions)],
            multiple: false,
            searchable: false,
            showLabels: false,
            hideSelected: true,
          }) },
          show: { component: 'b-tag', props: () => ({ type: 'is-primary', rounded: true }) },
        },
        'String': {
          name: this.$t('message.settings.columnsTab.types.text'), default: '',
          create: { component: 'b-input', props: () => ({
            placeholder: this.$t('message.settings.columnsTab.modal.defaultValuePlaceholder'),
            size: 'is-small',
          }) },
          show: { component: 'span', props: () => null },
        },
      },
    };
  },
  props: {
    entityConfiguration: {
      type: Object,
      default: () => {},
    },
    selectedExtraColumn: {
      type: Object,
      default: null,
    },
  },
  computed: {
    newExtraColumnValidFields() {
      return !(this.newExtraColumnNameBlank || this.newExtraTagEmpty);
    },
    newExtraColumnNameBlank() {
      return this.newExtraColumnName.replace(/\s/g, '') === '';
    },
    defaultValueComponent() {
      return this.newExtraColumnTypeMap[this.newExtraColumnType];
    },
    newExtraTagEmpty() {
      return this.newExtraColumnType === 'Enum' && this.extraColumnOptions.length < 1;
    },
    newExtraColumn() {
      const newColumnSchema = {
        'name': this.newExtraColumnName,
        'type': this.newExtraColumnType,
        'default': this.newExtraColumnDefault,
      };
      if (this.newExtraColumnType === 'Enum') {
        newColumnSchema.options = { 'tags': this.extraColumnOptions };
        if (this.newExtraColumnDefault === '-' && !this.extraColumnOptions.includes('-')) {
          newColumnSchema.options = { 'tags': ['-', ...this.extraColumnOptions] };
        }
      }

      return newColumnSchema;
    },
  },
  methods: {
    async addExtraColumn() {
      if (this.newExtraColumnValidFields) {
        this.loading = true;
        try {
          await EntityConfigurationsApi.addExtraColumn(this.entityConfiguration.id,
            { extraColumnSchema: this.newExtraColumn });
          this.$emit('close');
        } catch (error) {
          this.loading = false;
          this.extraColumnError = error.message;
        }
      } else {
        this.showErrors = true;
      }
    },
    async editExtraColumn() {
      if (this.newExtraColumnValidFields) {
        this.loading = true;
        try {
          await EntityConfigurationsApi.updateExtraColumn(this.entityConfiguration.id,
            this.selectedExtraColumn.name,
            this.newExtraColumn);
          this.$emit('close');
        } catch (error) {
          this.loading = false;
          this.extraColumnError = error.message;
        }
      } else {
        this.showErrors = true;
      }
    },
    setNewExtraColumnType(type) {
      this.newExtraColumnType = type;
      this.newExtraColumnDefault = this.newExtraColumnTypeMap[type].default;
      this.showErrors = false;
    },
    validateTag(tag) {
      if (!this.selectedExtraColumn) return true;

      return !this.selectedExtraColumn.options.tags.find((item) => item === tag);
    },
  },
  beforeMount() {
    if (this.selectedExtraColumn) {
      this.newExtraColumnName = this.selectedExtraColumn.name;
      this.newExtraColumnType = this.selectedExtraColumn.type;
      switch (this.newExtraColumnType) {
      case 'Date':
        this.newExtraColumnDefault = null;
        break;
      case 'Enum':
        this.newExtraColumnDefault = this.selectedExtraColumn.default;
        this.extraColumnOptions = this.selectedExtraColumn.options.tags;
        this.newExtraColumnOptions = [];
        break;
      default:
        this.newExtraColumnDefault = this.selectedExtraColumn.default;
      }
    }
  },
  watch: {
    newExtraColumnOptions(newOptions) {
      if (Array.isArray(newOptions)) {
        if (this.selectedExtraColumn && this.selectedExtraColumn.type === 'Enum') {
          this.extraColumnOptions = this.selectedExtraColumn.options.tags.concat(newOptions);
        } else {
          this.extraColumnOptions = newOptions;
        }
      }
    },
  },
};
</script>
